import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa, ViewType } from "@supabase/auth-ui-shared";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { authDialog } from "@/lib/localization";
import { supabase } from "@/lib/supabase/supabase";
import { useTheme } from "../lib/theme/theme-provider";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect } from "react";

const authConfig = {
  supabaseClient: supabase,
  // providers: ["google", "facebook"] as AuthProviders[],
  providers: [],
  showLinks: true,
  appearance: {
    theme: ThemeSupa,
    variables: {
      default: {
        colors: {
          brand: "#2563EB",
          brandAccent: "#2563EB/50",
          brandButtonText: "#2563EB/50",
          inputBackground: "transparent !important",
        },
      },
    },
  },
  localization: {
    variables: authDialog,
  },
  redirectTo: "/news",
};

export default function AuthDialog(props: {
  view?: ViewType;
  children: React.ReactNode;
  ghost?: boolean;
}) {
  const { theme } = useTheme();
  const { view, children, ghost } = props;

  const [isAccepted, setIsAccepted] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const normalizedTheme =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;

  // Check if user tries to interact with the auth form without accepting terms
  useEffect(() => {
    const handleAuthInteraction = (e: Event) => {
      if (!isAccepted) {
        e.preventDefault();
        e.stopPropagation();
        setShowWarning(true);
      }
    };

    const authForm = document.querySelector("#auth-dialog form");
    if (authForm) {
      authForm.addEventListener("submit", handleAuthInteraction);
      authForm.addEventListener("click", handleAuthInteraction, {
        capture: true,
      });
    }

    return () => {
      if (authForm) {
        authForm.removeEventListener("submit", handleAuthInteraction);
        authForm.removeEventListener("click", handleAuthInteraction, {
          capture: true,
        });
      }
    };
  }, [isAccepted]);

  const handleToggle = () => {
    setIsAccepted((prev) => !prev);
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {ghost ? (
          children
        ) : (
          <Button className="bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg hover:bg-blue-700 transition border border-white">
            {children}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent className="sm:h-auto h-full gap-0" id="auth-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Witaj, podróżny! </AlertDialogTitle>
          <AlertDialogDescription>
            <span>
              Dostęp do wszystkich funkcji tylko dla zarejestrowanych
              użytkowników.
            </span>
            <span className={"flex items-center space-x-2 mt-2"}>
              <Switch
                id="accept"
                onCheckedChange={handleToggle}
                checked={isAccepted}
                className="border border-blue-600 border-2"
              />
              <label
                htmlFor="accept"
                className="text-blue-600 text-sm font-bold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Aby korzystać z aplikacji, musisz zaakceptować{" "}
                <a className="underline" href="/terms" target="_blank">
                  Regulamin
                </a>
                .
              </label>
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div>
          <Auth {...authConfig} view={view} theme={normalizedTheme} />
        </div>

        {/* Warning Dialog */}
        {showWarning && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-sm mx-4">
              <h3 className="text-lg font-bold mb-4">
                Wymagana akceptacja regulaminu
              </h3>
              <p className="mb-4">
                Aby się zalogować, musisz zaakceptować regulamin aplikacji.
              </p>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowWarning(false)}>
                  Wróć
                </Button>
                <Button
                  onClick={() => {
                    setIsAccepted(true);
                    setShowWarning(false);
                  }}
                >
                  Akceptuję regulamin
                </Button>
              </div>
            </div>
          </div>
        )}
        <AlertDialogFooter className="-mt-2">
          <AlertDialogCancel>Anuluj</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
